import { useEffect, useState } from "react";
import { X, ArrowUpRight, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { parseGalleryItem } from "../lib/gallery";

function resolveUrl(url) {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

function GalleryThumb({ item, onClick, testid }) {
  const parsed = parseGalleryItem(item);
  const isMedia = parsed.type !== "image";
  const thumb = parsed.thumbUrl || resolveUrl(parsed.src);

  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="group relative w-full h-full overflow-hidden bg-[#121212]"
    >
      {parsed.type === "video" ? (
        <video src={resolveUrl(parsed.src)} muted playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      ) : (
        <img src={thumb} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      )}
      {isMedia && (
        <>
          <div className="absolute inset-0 bg-black/25 group-hover:bg-black/10 transition-colors" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="w-14 h-14 rounded-full bg-[#ff4500]/95 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
              <Play size={20} className="text-black ml-0.5" fill="currentColor" />
            </span>
          </div>
          <span className="absolute top-3 left-3 font-mono text-[9px] uppercase tracking-widest bg-black/60 backdrop-blur-md text-white px-2 py-1 border border-white/10">
            {parsed.type === "vimeo" ? "Vimeo" : parsed.type === "youtube" ? "YouTube" : "Video"}
          </span>
        </>
      )}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
    </button>
  );
}

function LightboxMedia({ item }) {
  const parsed = parseGalleryItem(item);
  const commonProps = {
    onClick: (e) => e.stopPropagation(),
  };
  if (parsed.type === "vimeo" || parsed.type === "youtube") {
    return (
      <iframe
        {...commonProps}
        src={parsed.embedUrl}
        title="video"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        className="w-full max-w-6xl aspect-video bg-black"
      />
    );
  }
  if (parsed.type === "video") {
    return (
      <video
        {...commonProps}
        src={resolveUrl(parsed.src)}
        autoPlay
        loop
        controls
        playsInline
        className="max-w-full max-h-[85vh] bg-black"
      />
    );
  }
  return (
    <img
      {...commonProps}
      src={resolveUrl(parsed.src)}
      alt=""
      className="max-w-full max-h-[85vh] object-contain"
    />
  );
}

export default function WorkModal({ project, onClose, onGoToContact }) {
  const { lang, t } = useLang();
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    if (!project) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (lightboxIdx !== null) setLightboxIdx(null);
        else onClose();
      }
      if (lightboxIdx !== null) {
        const len = project.gallery?.length || 0;
        if (!len) return;
        if (e.key === "ArrowRight") setLightboxIdx((i) => (i + 1) % len);
        if (e.key === "ArrowLeft") setLightboxIdx((i) => (i - 1 + len) % len);
      }
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [project, lightboxIdx, onClose]);

  if (!project) return null;

  const role = lang === "pt" ? project.role_pt || project.role_en : project.role_en;
  const shortDesc = lang === "pt" ? project.description_pt || project.description_en : project.description_en;
  const longDesc = lang === "pt" ? project.long_description_pt || project.long_description_en : project.long_description_en;
  const gallery = Array.isArray(project.gallery) ? project.gallery.filter(Boolean) : [];

  const heroMedia = project.video_url ? (
    <video
      src={resolveUrl(project.video_url)}
      autoPlay
      muted
      loop
      playsInline
      className="absolute inset-0 w-full h-full object-cover"
    />
  ) : (
    <img
      src={resolveUrl(project.image_url)}
      alt={project.title}
      className="absolute inset-0 w-full h-full object-cover"
    />
  );

  return (
    <div data-testid="work-modal" data-lenis-prevent className="fixed inset-0 z-[90] bg-[#0a0a0a] overflow-y-auto overscroll-contain animate-in fade-in">
      <div className="sticky top-0 z-10 bg-black/85 backdrop-blur-xl border-b border-white/5 px-6 md:px-12 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#ff4500]">[ Case Study ]</span>
          <span className="h-[1px] w-10 bg-[#262626] hidden md:block" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">{project.client}</span>
        </div>
        <button
          data-testid="work-modal-close"
          onClick={onClose}
          className="w-10 h-10 border border-[#262626] hover:border-[#ff4500] hover:bg-[#ff4500] flex items-center justify-center transition-all group"
          aria-label="Close"
        >
          <X size={16} className="text-white group-hover:text-black" />
        </button>
      </div>

      <div className="relative w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden bg-[#121212]">
        {heroMedia}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 pb-10 md:pb-16">
          <div className="max-w-[1440px] mx-auto">
            <div className="flex items-center gap-3 mb-4 font-mono text-[10px] uppercase tracking-[0.25em]">
              <span className="text-[#ff4500]">{role || project.client}</span>
              <span className="w-1 h-1 rounded-full bg-neutral-600" />
              <span className="text-neutral-400">{project.year}</span>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.95] text-white">
              {project.title}
            </h2>
          </div>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <aside className="lg:col-span-3">
            <dl className="space-y-8">
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Client</dt>
                <dd className="font-serif text-2xl font-light text-white">{project.client}</dd>
              </div>
              {role && (
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Role</dt>
                  <dd className="text-white leading-relaxed">{role}</dd>
                </div>
              )}
              {project.year && (
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Year</dt>
                  <dd className="text-white">{project.year}</dd>
                </div>
              )}
            </dl>
          </aside>

          <div className="lg:col-span-8 lg:col-start-5">
            {shortDesc && (
              <p className="font-serif text-2xl md:text-3xl lg:text-4xl leading-[1.25] font-light text-white mb-10 italic">
                {shortDesc}
              </p>
            )}
            {longDesc && (
              <div className="text-neutral-300 text-base md:text-lg leading-relaxed space-y-5 max-w-2xl">
                {longDesc.split(/\n\s*\n/).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}
            {!shortDesc && !longDesc && (
              <p className="text-neutral-500 italic">
                {lang === "pt" ? "Detalhes deste case em breve." : "Case study details coming soon."}
              </p>
            )}
          </div>
        </div>

        {gallery.length > 0 && (() => {
          const items = gallery.map((src, i) => ({ src, parsed: parseGalleryItem(src), origIdx: i }));
          const videos = items.filter((it) => it.parsed.type !== "image");
          const images = items.filter((it) => it.parsed.type === "image");
          return (
            <div className="mt-20 md:mt-32">
              <div className="flex items-center gap-4 mb-8">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#ff4500]">[ Gallery ]</span>
                <span className="h-[1px] w-16 bg-[#262626]" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                  {images.length} {images.length === 1 ? "image" : "images"}
                  {videos.length > 0 && ` · ${videos.length} ${videos.length === 1 ? "video" : "videos"}`}
                </span>
              </div>

              {/* Videos — full-width, horizontally centered on the page */}
              {videos.length > 0 && (
                <div className="space-y-6 md:space-y-8 mb-10 md:mb-14">
                  {videos.map((it) => (
                    <div key={`v-${it.origIdx}`} className="max-w-6xl mx-auto">
                      <div className="aspect-video">
                        <GalleryThumb item={it.src} onClick={() => setLightboxIdx(it.origIdx)} testid={`gallery-item-${it.origIdx}`} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Images — 3-column grid, centered on the page */}
              {images.length > 0 && (
                <div
                  className={`max-w-6xl mx-auto grid gap-3 md:gap-5 ${
                    images.length === 1
                      ? "grid-cols-1"
                      : images.length === 2
                      ? "grid-cols-1 md:grid-cols-2"
                      : "grid-cols-1 md:grid-cols-3"
                  }`}
                >
                  {images.map((it) => (
                    <div key={`i-${it.origIdx}`} className="aspect-[4/5]">
                      <GalleryThumb item={it.src} onClick={() => setLightboxIdx(it.origIdx)} testid={`gallery-item-${it.origIdx}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        <div className="mt-20 md:mt-32 border-t border-[#1a1a1a] pt-16 md:pt-20 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
              {lang === "pt" ? "Interessado num projeto assim?" : "Interested in a project like this?"}
            </div>
            <h3 className="font-serif text-3xl md:text-5xl font-light tracking-tight text-white max-w-xl">
              {lang === "pt" ? "Vamos conversar." : "Let's talk."}
            </h3>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="work-modal-cta"
              onClick={onGoToContact}
              className="btn-orange px-8 py-4 text-[12px] uppercase tracking-[0.25em] font-medium"
            >
              <span className="inline-flex items-center gap-2">{t.contact.primaryCta} <ArrowUpRight size={14} /></span>
            </button>
            <button onClick={onClose} className="btn-outline-orange px-8 py-4 text-[12px] uppercase tracking-[0.25em] font-medium">
              <span>{lang === "pt" ? "Voltar" : "Back"}</span>
            </button>
          </div>
        </div>
      </div>

      {lightboxIdx !== null && gallery[lightboxIdx] && (
        <div
          data-lenis-prevent
          className="fixed inset-0 z-[110] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10 overscroll-contain"
          onClick={() => setLightboxIdx(null)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i - 1 + gallery.length) % gallery.length); }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 border border-white/20 hover:border-[#ff4500] hover:bg-[#ff4500] flex items-center justify-center transition-all group z-10"
            aria-label="Previous"
          >
            <ChevronLeft className="text-white group-hover:text-black" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i + 1) % gallery.length); }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 border border-white/20 hover:border-[#ff4500] hover:bg-[#ff4500] flex items-center justify-center transition-all group z-10"
            aria-label="Next"
          >
            <ChevronRight className="text-white group-hover:text-black" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            className="absolute top-6 right-6 w-11 h-11 border border-white/20 hover:border-[#ff4500] hover:bg-[#ff4500] flex items-center justify-center transition-all group z-10"
            aria-label="Close lightbox"
          >
            <X size={16} className="text-white group-hover:text-black" />
          </button>
          <LightboxMedia item={gallery[lightboxIdx]} />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            {lightboxIdx + 1} / {gallery.length}
          </div>
        </div>
      )}
    </div>
  );
}
